<script setup>
import { computed, ref } from 'vue';
import XlButton from '../../xl/XlButton.vue';
import XlListRow from '../../xl/XlListRow.vue';
import IconChevronRight from '../../icons/IconChevronRight.vue';

const props = defineProps({
  row: { type: Object, required: true },
  showDay: { type: Boolean, default: false },
  // A row appears in two places, and the week's copy needs its own handle.
  // Taken as a prop rather than left to attribute fallthrough: binding
  // `data-testid` on the tag silently OVERWROTE the one this component sets on
  // its own root, so week rows were unreachable by the name they advertise.
  testid: { type: String, default: '' },
});

const emit = defineEmits(['open', 'act']);

const anchor = ref(null);
defineExpose({ anchor });

const hasAction = computed(() => props.row.action?.kind && props.row.action.kind !== 'none');

// The time carries the day only on week rows — "Fri 2 PM" — so today's rows
// stay as short as they can be.
const when = computed(() =>
  props.showDay && props.row.day_label
    ? `${props.row.day_label} ${props.row.start_label}`
    : props.row.start_label
);
</script>

<template>
  <div
    ref="anchor"
    class="schedule-row"
    :class="{ 'schedule-row--highlight': row.highlight }"
    :data-testid="props.testid || `schedule-row-${row.id}`"
    role="button"
    tabindex="0"
    @click="emit('open', row, anchor)"
    @keydown.enter="emit('open', row, anchor)"
    @keydown.space.prevent="emit('open', row, anchor)"
  >
    <!--
      The same row the Policyholders page lists invitations with. The clickable
      wrapper stays outside it: XlListRow is presentation only, so it cannot own
      the role, the tabindex or the keyboard handling above.
    -->
    <XlListRow>
      <template #leading>
        <span class="schedule-row__time" data-testid="row-time">{{ when }}</span>
        <span
          class="schedule-row__dot"
          :class="`schedule-row__dot--${row.kind}`"
          data-testid="row-dot"
          aria-hidden="true"
        />
      </template>

      <template #title>{{ row.title }}</template>

      <template #subtitle>
        <span data-testid="row-subline">{{ row.subline }}</span>
      </template>

      <template #trailing>
        <!--
          Exactly one action per row. `.stop` because the row itself opens the
          popover: without it, opening the room would also open a panel over it.
        -->
        <XlButton
          v-if="hasAction"
          variant="ghost"
          size="sm"
          data-testid="row-action"
          @click.stop="emit('act', row)"
        >
          {{ row.action.label }}
        </XlButton>
        <!-- currentColor rather than a hex, so the chevron follows the token on
             the span below instead of pinning one generation's grey in. -->
        <span v-else class="schedule-row__chevron" data-testid="row-chevron" aria-hidden="true">
          <IconChevronRight color="currentColor" />
        </span>
      </template>
    </XlListRow>
  </div>
</template>

<style scoped>
/* The wrapper owns separation and interaction; XlListRow owns layout and
   type. The border lives here rather than on XlListRow because each row is an
   only child of its own wrapper, so XlListRow's `:last-child` rule would
   suppress every one of them. */
.schedule-row {
  position: relative;
  border-top: 1px solid var(--xl-color-border);
  cursor: pointer;
}

.schedule-row:first-child {
  border-top: 0;
}

/*
  The hover fill is a layer of its own, not a background on the row.

  Painted on the row it stopped dead at the text, flush against the card's
  padding, which reads as a clipped rectangle rather than a highlight. This
  bleeds 10px into that padding and rounds off, the way the board draws it —
  and because it is positioned rather than in flow, the row's own height,
  the divider above it and everything inside stay exactly where they were.
*/
.schedule-row::before {
  content: '';
  position: absolute;
  inset: 1px -10px;
  border-radius: 10px;
  pointer-events: none;
}

/*
  The same tint the brokers table hovers with, which is the board's --tint.
  This was a 4% wash of the text colour — a grey where the rest of the page
  goes lilac, so two lists on one page answered the same gesture differently.
*/
.schedule-row:hover::before {
  background: var(--xl-color-primary-container);
}

/* Above the fill: a positioned pseudo-element otherwise paints over the
   in-flow content it is meant to sit behind. */
.schedule-row :deep(.xl-list-row) {
  position: relative;
}

.schedule-row:focus-visible {
  outline: 2px solid var(--xl-color-primary);
  outline-offset: -2px;
}

/* Reserved for somebody on duty with people waiting. Nothing sets it yet —
   there is no queue to know from — so it never renders today. Styled from the
   warning token anyway, so the day it does fire it is not invisible. */
.schedule-row--highlight {
  background: color-mix(in srgb, var(--xl-color-warning) 12%, white);
}

.schedule-row__time {
  min-width: 64px;
  font-size: 13px;
  color: var(--xl-color-outline);
  font-variant-numeric: tabular-nums;
}

.schedule-row__dot {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: 3px;
}

.schedule-row__dot--session {
  background: var(--xl-color-primary);
}

.schedule-row__dot--office_hours {
  /* No success token in this generation; the fallback is the same green the
     Northstar set resolves to. */
  background: var(--xl-color-success, #166b48);
}

/* Title and subline are XlListRow's elements, so reaching them takes :deep().
   Only the truncation is set here — the sizes and colours are the kit's, which
   is the point of using it. */
:deep(.xl-list-row__text b),
:deep(.xl-list-row__text > span) {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.schedule-row__chevron {
  display: flex;
  flex-shrink: 0;
  color: var(--xl-color-outline);
}
</style>
